import React from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import api from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import { useToast } from '@/hooks/use-toast'
import { Users, Shield, Eye, ShieldCheck, UserPlus, Archive, RotateCcw, ChevronsUpDown, Check, Loader2 } from 'lucide-react'

const roleConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  org_owner: { label: 'Owner', icon: ShieldCheck, color: 'text-amber-600' },
  org_admin: { label: 'Admin', icon: Shield, color: 'text-blue-600' },
  org_viewer: { label: 'Viewer', icon: Eye, color: 'text-slate-500' },
}

type MemberRow = {
  id: string
  user_id?: string
  role: string
  status: string
  created_at?: string
  user?: {
    id?: string
    first_name?: string
    last_name?: string
    email?: string
  }
  first_name?: string
  last_name?: string
  email?: string
}

type CandidateUser = {
  id: string
  first_name?: string
  last_name?: string
  email?: string
}

const toTitleCase = (value: string) =>
  value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const getErrorMessage = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
  return message || fallback
}

const OrgMembers: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const { orgRole } = useOrganization()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const canManageMembers = orgRole === 'org_owner' || orgRole === 'org_admin'
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false)
  const [selectedUserId, setSelectedUserId] = React.useState('')
  const [selectedRole, setSelectedRole] = React.useState<'org_owner' | 'org_admin' | 'org_viewer'>('org_viewer')
  const [userSearchTerm, setUserSearchTerm] = React.useState('')
  const [debouncedUserSearchTerm, setDebouncedUserSearchTerm] = React.useState('')
  const [userDropdownOpen, setUserDropdownOpen] = React.useState(false)
  const [archiveTarget, setArchiveTarget] = React.useState<{ userId: string; name: string } | null>(null)
  const [statusMutatingUserId, setStatusMutatingUserId] = React.useState<string | null>(null)

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedUserSearchTerm(userSearchTerm.trim())
    }, 300)

    return () => window.clearTimeout(timer)
  }, [userSearchTerm])

  React.useEffect(() => {
    setSelectedUserId('')
  }, [debouncedUserSearchTerm])

  const { data: membersData, isLoading } = useQuery({
    queryKey: ['org-members', orgId],
    queryFn: async () => {
      const res = await api.get(`/organizations/${orgId}/members`)
      return res.data?.data
    },
    enabled: !!orgId,
  })

  const members: MemberRow[] = membersData?.members || []
  const { data: candidateUsersData, isFetching: isCandidateUsersFetching } = useQuery({
    queryKey: ['org-member-candidates', orgId, debouncedUserSearchTerm],
    queryFn: async () => {
      const res = await api.get(`/organizations/${orgId}/users/search`, {
        params: { q: debouncedUserSearchTerm || undefined, limit: 100 },
      })
      return res.data?.data
    },
    enabled: !!orgId && canManageMembers && isAddDialogOpen,
  })
  const candidateUsers: CandidateUser[] = candidateUsersData?.users || []
  const selectedCandidateUser = candidateUsers.find((user) => user.id === selectedUserId)

  const addMemberMutation = useMutation({
    mutationFn: async (payload: { user_id: string; role: 'org_owner' | 'org_admin' | 'org_viewer' }) => {
      await api.post(`/organizations/${orgId}/members`, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-members', orgId] })
      queryClient.invalidateQueries({ queryKey: ['org-member-candidates', orgId] })
      setSelectedUserId('')
      setSelectedRole('org_viewer')
      setUserSearchTerm('')
      setDebouncedUserSearchTerm('')
      setIsAddDialogOpen(false)
      toast({ title: 'Member added', description: 'Organization member created successfully.' })
    },
    onError: (error: unknown) => {
      toast({
        title: 'Unable to add member',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      })
    },
  })

  const updateMemberStatusMutation = useMutation({
    mutationFn: async (payload: { userId: string; status: 'active' | 'suspended' }) => {
      setStatusMutatingUserId(payload.userId)
      await api.patch(`/organizations/${orgId}/members/${payload.userId}/status`, { status: payload.status })
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['org-members', orgId] })
      const action = variables.status === 'suspended' ? 'archived' : 'unarchived'
      toast({ title: 'Status updated', description: `Member ${action} successfully.` })
    },
    onError: (error: unknown) => {
      toast({
        title: 'Unable to update member status',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      })
    },
    onSettled: () => {
      setStatusMutatingUserId(null)
    },
  })

  const handleCreateMember = () => {
    if (!selectedUserId) {
      toast({ title: 'Select a user', description: 'Please choose a user to add.', variant: 'destructive' })
      return
    }

    addMemberMutation.mutate({ user_id: selectedUserId, role: selectedRole })
  }

  const handleArchiveMember = async () => {
    if (!archiveTarget) return
    await updateMemberStatusMutation.mutateAsync({ userId: archiveTarget.userId, status: 'suspended' })
    setArchiveTarget(null)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Members</h1>
          <p className="text-sm text-slate-500 mt-1">
            {members.length} member{members.length !== 1 ? 's' : ''} in your organization
          </p>
        </div>
        {canManageMembers && (
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <UserPlus className="w-4 h-4 mr-1" />
            Add Member
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No members yet</h3>
            <p className="text-sm text-slate-500 mt-1">Members will appear here once added</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Organization Members</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-3 font-medium">Member</th>
                    <th className="pb-3 font-medium">Role</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Joined</th>
                    {canManageMembers && <th className="pb-3 font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => {
                    const role = roleConfig[member.role as string] || roleConfig.org_viewer
                    const RoleIcon = role.icon
                    const userId = member.user?.id || member.user_id || ''
                    const firstName = member.user?.first_name || member.first_name || ''
                    const lastName = member.user?.last_name || member.last_name || ''
                    const email = member.user?.email || member.email || '—'
                    const displayName = `${firstName} ${lastName}`.trim() || 'Unknown User'
                    const isSuspended = member.status === 'suspended'
                    const isUpdating = statusMutatingUserId === userId && updateMemberStatusMutation.isPending

                    return (
                      <tr key={member.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-3">
                          <div>
                            <p className="font-medium text-slate-900">
                              {displayName}
                            </p>
                            <p className="text-xs text-slate-400">{email}</p>
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1.5">
                            <RoleIcon className={`w-4 h-4 ${role.color}`} />
                            <span className={role.color}>{role.label}</span>
                          </div>
                        </td>
                        <td className="py-3">
                          <Badge variant={(member.status as string) === 'active' ? 'default' : 'secondary'}>
                            {toTitleCase(member.status)}
                          </Badge>
                        </td>
                        <td className="py-3 text-slate-500">
                          {member.created_at
                            ? new Date(member.created_at as string).toLocaleDateString()
                            : '—'}
                        </td>
                        {canManageMembers && (
                          <td className="py-3">
                            {isSuspended ? (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!userId || isUpdating}
                                onClick={() => updateMemberStatusMutation.mutate({ userId, status: 'active' })}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Unarchive
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!userId || isUpdating}
                                onClick={() => setArchiveTarget({ userId, name: displayName })}
                              >
                                <Archive className="w-4 h-4 mr-1" />
                                Archive
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Organization Member</DialogTitle>
            <DialogDescription>
              Owners and admins can add members to this organization.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="member-user-search">User <span className="text-red-500">*</span></Label>
              <Popover open={userDropdownOpen} onOpenChange={setUserDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="member-user-search"
                    variant="outline"
                    role="combobox"
                    aria-expanded={userDropdownOpen}
                    className="w-full justify-between font-normal"
                  >
                    {selectedCandidateUser
                      ? `${`${selectedCandidateUser.first_name || ''} ${selectedCandidateUser.last_name || ''}`.trim() || selectedCandidateUser.email || selectedCandidateUser.id}${selectedCandidateUser.email ? ` (${selectedCandidateUser.email})` : ''}`
                      : 'Search and select a user'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-0"
                  align="start"
                  onWheel={(event) => event.stopPropagation()}
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search users by name or email..."
                      value={userSearchTerm}
                      onValueChange={(value) => setUserSearchTerm(value)}
                    />
                    <CommandList>
                      {isCandidateUsersFetching && (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                        </div>
                      )}
                      {!isCandidateUsersFetching && (
                        <CommandEmpty>
                          {debouncedUserSearchTerm ? 'No users found for this search' : 'No available users'}
                        </CommandEmpty>
                      )}
                      {!isCandidateUsersFetching && candidateUsers.length > 0 && (
                        <CommandGroup className="max-h-[260px] overflow-y-auto">
                          {candidateUsers.map((user) => {
                            const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || user.id
                            const label = `${name}${user.email ? ` (${user.email})` : ''}`

                            return (
                              <CommandItem
                                key={user.id}
                                value={label}
                                onSelect={() => {
                                  setSelectedUserId(user.id)
                                  setUserDropdownOpen(false)
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${selectedUserId === user.id ? 'opacity-100' : 'opacity-0'}`} />
                                {label}
                              </CommandItem>
                            )
                          })}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Role <span className="text-red-500">*</span></Label>
              <Select value={selectedRole} onValueChange={(value: 'org_owner' | 'org_admin' | 'org_viewer') => setSelectedRole(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {orgRole === 'org_owner' && <SelectItem value="org_owner">Owner</SelectItem>}
                  <SelectItem value="org_admin">Admin</SelectItem>
                  <SelectItem value="org_viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateMember} isLoading={addMemberMutation.isPending}>
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null)
        }}
        title="Archive member?"
        description={archiveTarget ? `Archive ${archiveTarget.name}? They can be unarchived later.` : 'Archive this member?'}
        onConfirm={handleArchiveMember}
        isLoading={updateMemberStatusMutation.isPending}
      />

      {/* Role permissions info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Role Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 font-medium text-amber-600">
                <ShieldCheck className="w-4 h-4" />
                Owner
              </div>
              <p className="text-slate-500">
                Full access to all org settings, clinics, and members. Can add/remove clinics and members.
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 font-medium text-blue-600">
                <Shield className="w-4 h-4" />
                Admin
              </div>
              <p className="text-slate-500">
                Can view and manage org settings and clinics. Can add members but cannot remove owners.
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 font-medium text-slate-500">
                <Eye className="w-4 h-4" />
                Viewer
              </div>
              <p className="text-slate-500">
                Read-only access to org dashboard and clinic data. No PHI access or write operations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default OrgMembers
